import { createSlice } from '@reduxjs/toolkit'
import { RootState, AppThunk } from './store'
import { get, postForm } from '../utils/rest'
import { OrganisationPage } from 'types/types'
import * as Toast from '../components/Toast'

export interface OrganisationState {
  isLoading: boolean
  organisation: OrganisationPage | null
  isNotFound: boolean
}

const createInitialState = (): OrganisationState => {
  return {
    isLoading: false,
    organisation: null,
    isNotFound: false,
  }
}

export const organisationSlice = createSlice({
  name: 'site',
  initialState: createInitialState(),
  reducers: {
    fetchOrganisationBegin: (state) => {
      state.isLoading = true
    },
    fetchOrganisationSuccess: (state, action) => {
      state.isLoading = false
      state.organisation = action.payload
    },
    fetchOrganisationFail: (state) => {
      state.isLoading = false
      state.isNotFound = true
    },
    editOrganisationBegin: (state) => {
      state.isLoading = true
    },
    editOrganisationSuccess: (state) => {
      state.isLoading = false
    },
    editOrganisationFail: (state) => {
      state.isLoading = false
    },
  },
})

export const {
  fetchOrganisationBegin,
  fetchOrganisationSuccess,
  fetchOrganisationFail,
  editOrganisationBegin,
  editOrganisationSuccess,
  editOrganisationFail,
} = organisationSlice.actions

export const loadOrganisationAsync =
  (orgId: string | undefined): AppThunk =>
  async (dispatch) => {
    dispatch(fetchOrganisationBegin())

    try {
      const response = await get<OrganisationPage>(`/api/organisation/${orgId}`)
      if (response.status === 200 && response.parsedBody?.data) {
        dispatch(fetchOrganisationSuccess(response.parsedBody?.data))
      } else {
        Toast.dispatchToastError({
          message: response.parsedBody?.message ?? 'Fel vid hämtning av organisation',
          correlationId: response.parsedBody?.correlationId,
        })
        dispatch(fetchOrganisationFail())
      }
    } catch (e) {
      Toast.dispatchToastError({ message: 'Fel vid hämtning av organisation' })
      dispatch(fetchOrganisationFail())
    }
  }

export const editOrganisationAsync =
  (organisation: OrganisationPage, organisationLogotype: FileList | null): AppThunk =>
  async (dispatch) => {
    dispatch(editOrganisationBegin())
    try {
      const formData = new FormData()
      const logotype = organisationLogotype != null ? organisationLogotype[0] : ''

      formData.append('organisation', JSON.stringify(organisation))
      formData.append('logotype', logotype)

      const res = await postForm('/api/organisation/save', formData)

      if (res.status === 200 || res.status === 204) {
        dispatch(editOrganisationSuccess())
        Toast.dispatchToastSuccess({ message: res.parsedBody?.message ?? 'Sparade organisationen' })
      } else {
        dispatch(editOrganisationFail())
        Toast.dispatchToastError({
          message: res.parsedBody?.message ?? 'Kunde inte spara organisationen',
          correlationId: res.parsedBody?.correlationId,
        })
      }
    } catch {
      dispatch(editOrganisationFail())
      Toast.dispatchToastError({ message: 'Fel vid sparande av organisation' })
    }
  }

export const selectOrganisationState = (state: RootState): OrganisationState => state.organisation
export const selectOrganisation = (state: RootState): OrganisationPage | null => state.organisation.organisation

export default organisationSlice.reducer
